/**
 * What a question brings to the Assistant page, from anywhere in the console
 * (docs/plan/platform-assistant.md §5, owner decision 21).
 *
 * THE ASSISTANT IS A PAGE: "#assistant" on the SOC route, drawn in the main
 * area like every other page, with only its rail entry lit. It was a dock over
 * whichever page was open (2026-10-17: the URL kept saying "#policies" under
 * it, and the rail lit two entries). This provider no longer draws anything:
 * it holds what the page is ABOUT — the subject a drill hands over (the About
 * chip), the question to ask on arrival, and where "← Back to <page>" returns —
 * and it knows how to reach the page from wherever the operator is.
 *
 * WHY A PROVIDER AND NOT A PROP: the console is a multi-page app — soc, choke,
 * devices, vpn and fleet are separate entries with no shared router. A drill
 * panel on ANY of them asks the assistant through this one seam:
 *  - on the SOC route the route registers its page opener (setPageOpener), and
 *    the page opens in place;
 *  - on the other documents there is no Assistant page, so the context is
 *    carried (carry.ts, with its rules) and the SOC route is loaded on
 *    "/#assistant", where the record is taken and checked before anything of
 *    it is shown.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { type AssistantSurface } from "./api";
import { ASSISTANT_DOCUMENT, clearCarry, documentLabel, leaveCarry, sessionNow, takeCarry, whoName, type Carry } from "./carry";
import {
  backLabel,
  drillAddress,
  drillWords,
  execIdOf,
  normalizeSubject,
  subjectKey,
  type AssistantSubjectRef,
  type DrillConversation
} from "./handover";
import { clearKept, clearKeptDraft, readKept, readKeptDraft } from "./keptConversation";
import { pendingTenantHydration, selectedTenantNow, tenantScopeRefusal, useSelectedTenant } from "../../lib/tenantScope";
import { getJSON } from "../../lib/api";

export interface AssistantHandover {
  /** The subject under investigation: an exec id, "device:<mac>", "vpn:<peer>", "web:<client>", "change:<id>". */
  execId?: string;
  /** A question to ask on open. */
  question?: string;
  /** Tenant or host this console is showing. */
  scopeLabel?: string;
  /**
   * The panel the handover came from, so the page opens knowing not just
   * WHICH subject is under investigation but WHAT KIND OF PANEL the analyst
   * was standing on. Without it, a question from Devices Assurance arrived
   * at an assistant that had forgotten it was ever about devices.
   */
  surface?: AssistantSurface;
  /**
   * The subject, typed (handover.ts): the About chip names it and "← Back to"
   * names the drill. Where `execId` is absent, the subject's own string is
   * the one the page asks about.
   */
  subject?: AssistantSubjectRef;
  /**
   * The conversation in the drill's chat: it continues on the page (owner
   * decision 22's tab-kept conversation on the engine). Absent for a
   * handover from no chat (a narration, a graph node's Ask): those ask
   * their question in the conversation on the page.
   */
  conversation?: DrillConversation;
}

/** What the conversation is about: the About chip, and the framing sent with each question. */
export interface AssistantSubject {
  execId?: string;
  surface?: AssistantSurface;
  scopeLabel?: string;
  /** The subject, typed, when a drill named it: what the About chip says. */
  ref?: AssistantSubjectRef;
}

/** A drill chat's conversation handed to the page, taken once (by its seq). */
export interface HandedConversation {
  seq: number;
  conversation: DrillConversation;
  /** What it is about (handover.ts subjectKey), or null when the drill named no subject. */
  about: string | null;
}

/** Where the operator was when a question brought them to the page. */
export interface AssistantReturn {
  /** The page's name, as its own header says it: "Alerts", "Dashboard", "Devices". */
  label: string;
  go: () => void;
  /**
   * The console address it goes to, where one is known (r4): what the record
   * kept for the tab stores, so "Go back to it" after a reload brings back
   * the previous conversation's Back with it (handover.ts keptAbout).
   */
  href?: string;
}

/**
 * The SOC route's way to open its Assistant page. Called for a question from
 * one of its own surfaces, with the drill's words (handover.ts drillWords)
 * when a drill handed it over, so "← Back to" names the page and the drill;
 * returns where the operator was, or null when there is nowhere to go back to.
 */
export type AssistantPageOpener = (drill?: string | null) => AssistantReturn | null;

interface AssistantChatValue {
  subject: AssistantSubject;
  /** The question a handover asked for, sent once by the page (by its id). */
  pendingQuestion: { id: number; text: string } | null;
  /** The drill chat's conversation a handover brought, continued once by the page (by its seq). */
  handed: HandedConversation | null;
  /** "← Back to <page>", or null when the page was opened from the rail, the palette or its address. */
  back: AssistantReturn | null;
  /** Opens the Assistant page, optionally carrying a subject and a question with it. */
  openAssistant: (handover?: AssistantHandover) => void;
  /** The About chip's ✕: the questions that follow are about the whole console. */
  clearSubject: () => void;
  /** The page was opened without a question's context: there is no "Back to". */
  forgetBack: () => void;
  /** The SOC route registers its page; null where a document has none. */
  setPageOpener: (open: AssistantPageOpener | null) => void;
  /**
   * The operator acted on the page (typed, chose or started a conversation):
   * a carried context still on its way is dropped rather than laid over it.
   */
  operatorActed: () => void;
}

const Ctx = createContext<AssistantChatValue | null>(null);

/**
 * useAssistantChat returns null when no provider is mounted.
 *
 * Null rather than a throw, deliberately. A drill panel rendered inside a test,
 * a Storybook story, or an entry that has not adopted the provider yet must
 * still render — the handover button simply does not appear. An assistant
 * convenience must never be able to blank a containment surface.
 */
export function useAssistantChat(): AssistantChatValue | null {
  return useContext(Ctx);
}

/** The address of the Assistant page, from any of the console's documents. */
export const ASSISTANT_ADDRESS = `${ASSISTANT_DOCUMENT}#assistant`;

export function AssistantChatProvider({
  children,
  navigate = (href: string) => window.location.assign(href)
}: {
  children: ReactNode;
  /** How another document is loaded; injected by tests (jsdom loads no documents). */
  navigate?: (href: string) => void;
}) {
  const [subject, setSubject] = useState<AssistantSubject>({});
  const [pendingQuestion, setPendingQuestion] = useState<{ id: number; text: string } | null>(null);
  const [handed, setHanded] = useState<HandedConversation | null>(null);
  const [back, setBack] = useState<AssistantReturn | null>(null);
  const questionSeq = useRef(0);
  const handSeq = useRef(0);

  /** A drill chat's conversation, for the page to continue — only this customer's, never another's. */
  const hand = useCallback((conversation: DrillConversation | undefined, about: AssistantSubjectRef | undefined) => {
    if (!conversation) return;
    handSeq.current += 1;
    setHanded({
      seq: handSeq.current,
      // Asked under another customer than this tab's now: its turns stay behind.
      conversation: conversation.tenant === selectedTenantNow() ? conversation : { ...conversation, messages: [] },
      about: about ? subjectKey(about) : null
    });
  }, []);
  // A ref, not state: registering is not something any render depends on, and
  // a state write here would re-render the whole console on every route render.
  const pageOpener = useRef<AssistantPageOpener | null>(null);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const ask = useCallback((text: string | undefined) => {
    if (!text || !text.trim()) return;
    questionSeq.current += 1;
    setPendingQuestion({ id: questionSeq.current, text });
  }, []);

  const setPageOpener = useCallback((open: AssistantPageOpener | null) => {
    pageOpener.current = open;
  }, []);

  /**
   * NOT ON THE SOC ROUTE: the context goes with the operator to the page.
   * Stamped with the account signed in now (whoami), this tab's own customer
   * and this session; where who is signed in cannot be told, nothing is
   * carried and the page simply opens.
   */
  const goToAssistantPage = useCallback(async (next?: AssistantHandover) => {
    if (next) {
      try {
        const who = whoName(await getJSON<unknown>("/api/whoami", { redirectOn401: false }));
        const here = window.location;
        const label = documentLabel(here.pathname);
        const tenant = selectedTenantNow();
        const execId = next.execId ?? execIdOf(next.subject);
        if (who) {
          leaveCarry({
            at: Date.now(),
            to: ASSISTANT_DOCUMENT,
            tenant,
            user: who,
            session: sessionNow(),
            ...(execId ? { execId } : {}),
            ...(next.scopeLabel ? { scopeLabel: next.scopeLabel } : {}),
            ...(next.surface ? { surface: next.surface } : {}),
            ...(next.question ? { question: next.question } : {}),
            // "← Back to <page> · <drill>", to the drill's own address on its document.
            ...(label ? { back: { label: backLabel(label, next.subject), href: drillAddress(next.subject, here) } } : {}),
            ...(next.subject ? { subject: next.subject } : {}),
            // The drill chat's turns, only when asked under this tab's customer now.
            ...(next.conversation && next.conversation.tenant === tenant ? { conversation: next.conversation } : {})
          });
        }
      } catch {
        // Who is signed in cannot be told: the page opens without the context.
      }
    }
    navigateRef.current(ASSISTANT_ADDRESS);
  }, []);

  const operatorFirst = useRef(false);
  const carryApplied = useRef(false);

  const openAssistant = useCallback(
    (given?: AssistantHandover) => {
      // The subject made to fit the rules it is read back by (a long title never sinks a carry).
      const next = given ? { ...given, subject: normalizeSubject(given.subject) } : undefined;
      const opener = pageOpener.current;
      if (!opener) {
        void goToAssistantPage(next);
        return;
      }
      // Only replace the subject when the caller supplies a handover, so an
      // open with nothing to say does not wipe what a drill just handed over.
      if (next) {
        // The operator's own question, asked now: nothing still on its way
        // from another page is laid over it.
        operatorFirst.current = true;
        setSubject({ execId: next.execId ?? execIdOf(next.subject), surface: next.surface, scopeLabel: next.scopeLabel, ref: next.subject });
        hand(next.conversation, next.subject);
        ask(next.question);
      }
      // Where it was asked, read before the opener leaves it: the address its Back keeps (r4).
      const here = { pathname: window.location.pathname, search: window.location.search, hash: window.location.hash };
      const from = opener(next ? drillWords(next.subject) : null);
      if (next) setBack(from ? { ...from, href: from.href ?? drillAddress(next.subject, here) } : from);
    },
    [ask, hand, goToAssistantPage]
  );

  const clearSubject = useCallback(() => setSubject({}), []);
  const forgetBack = useCallback(() => setBack(null), []);
  const operatorActed = useCallback(() => {
    operatorFirst.current = true;
  }, []);

  // THE CARRY ON THIS PAGE (carry.ts): taken once when the console mounts, and
  // applied only once whoami names the account that left it and this page's
  // tenant hydration has settled on the customer it was left for — and only if
  // the operator has not acted on the page meanwhile: the operator's own action
  // is never overwritten by a late carry. Held in a ref because the record
  // leaves storage when read and a mount effect may run twice (React's
  // development checks).
  const carried = useRef<Carry | null | undefined>(undefined);
  useEffect(() => {
    if (carried.current === undefined) carried.current = takeCarry();
    const carry = carried.current;
    if (!carry || carryApplied.current) return;
    let live = true;
    void Promise.all([getJSON<unknown>("/api/whoami", { redirectOn401: false }), pendingTenantHydration() ?? Promise.resolve()])
      .then(([who]) => {
        if (!live || carryApplied.current || operatorFirst.current) return;
        // A page whose customer is not confirmed (hydration ended at its
        // deadline, not by an answer) has not settled: the roster may still
        // adopt the remembered customer, under which a record stamped for no
        // customer must not land. Nothing is carried.
        if (tenantScopeRefusal() !== null) return;
        // Another account, or a page under another customer (a null stamp is
        // not a page that has one): not theirs.
        if (whoName(who) !== carry.user || carry.tenant !== selectedTenantNow()) return;
        carryApplied.current = true;
        setSubject({ execId: carry.execId, surface: carry.surface, scopeLabel: carry.scopeLabel, ref: carry.subject });
        // Its conversation was stamped with the record's customer (carry.ts), confirmed just above.
        hand(carry.conversation, carry.subject);
        ask(carry.question);
        const to = carry.back;
        if (to) setBack({ label: to.label, href: to.href, go: () => navigateRef.current(to.href) });
      })
      // Who is signed in, or which customer this page is, cannot be told: nothing is carried.
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // Once per page: a carry is a navigation's, not a render's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SIGN OUT ENDS ANY CARRY, AND THE CONVERSATION AND THE QUESTION KEPT FOR
  // THIS TAB, wherever it is pressed: none may outlive the session in this
  // tab's storage.
  useEffect(() => {
    const onSignOut = (event: MouseEvent) => {
      const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (!(link instanceof HTMLAnchorElement)) return;
      try {
        if (new URL(link.href, window.location.href).pathname === "/api/logout") {
          clearCarry();
          clearKept();
          clearKeptDraft();
        }
      } catch {
        // Not a URL: not a sign-out.
      }
    };
    document.addEventListener("click", onSignOut, true);
    return () => document.removeEventListener("click", onSignOut, true);
  }, []);

  // A CUSTOMER SWITCH, on any of the console's documents, ends the
  // conversation, and the question being written, kept for another customer
  // (keptConversation.ts). Only a switch: the page settling on its customer as
  // it loads is not one, so changes are read only once tenant hydration has
  // settled.
  const tenantNow = useSelectedTenant();
  const hydrated = useRef(false);
  const tenantSettled = useRef<string | null>(null);
  useEffect(() => {
    let live = true;
    void (pendingTenantHydration() ?? Promise.resolve()).then(() => {
      if (!live) return;
      hydrated.current = true;
      tenantSettled.current = selectedTenantNow();
    });
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    if (!hydrated.current || tenantSettled.current === tenantNow) return;
    tenantSettled.current = tenantNow;
    const kept = readKept();
    if (kept && kept.tenant !== tenantNow) clearKept();
    const draft = readKeptDraft();
    if (draft && draft.tenant !== tenantNow) clearKeptDraft();
  }, [tenantNow]);

  const value = useMemo(
    () => ({ subject, pendingQuestion, handed, back, openAssistant, clearSubject, forgetBack, setPageOpener, operatorActed }),
    [subject, pendingQuestion, handed, back, openAssistant, clearSubject, forgetBack, setPageOpener, operatorActed]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
